import { fetchAuthSession } from 'aws-amplify/auth';
import { ensureAmplifyConfigured, type RuntimeConfig } from './runtime-config';

export type RepoSummary = {
  repo_id: string;
  repo_name: string;
  created_at: string;
};

export type RepoStatus = {
  state: string;
  message?: string;
  last_sync_unix_ms?: number;
  upload_count?: number;
  download_count?: number;
};

export type RepoEntry = {
  path: string;
  type: 'file' | 'dir';
  size?: number;
  mtime?: number;
  mtime_unix_ms?: number;
  is_pinned?: boolean;
  is_hydrated?: boolean;
};

export type RepoEntriesResponse = {
  path: string;
  entries: RepoEntry[];
  next_page_token?: string;
};

export type RepoDevice = {
  device_id: string;
  device_name?: string;
  platform?: string;
  last_seen_unix_ms?: number;
  last_sync_unix_ms?: number;
  state?: string;
  message?: string;
  user_upn?: string;
};

export class WebApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const ensureIdToken = async (): Promise<string> => {
  await ensureAmplifyConfigured();
  let session = await fetchAuthSession();
  let token = session.tokens?.idToken?.toString() ?? null;

  if (!token) {
    session = await fetchAuthSession({ forceRefresh: true });
    token = session.tokens?.idToken?.toString() ?? null;
  }

  if (!token) {
    throw new WebApiError(401, 'NotAuthenticated', 'You need to sign in before calling the API.');
  }

  return token;
};

const getWebApiBaseUrl = (config: RuntimeConfig): string => {
  const apiUrl = config.api?.webApiUrl ?? config.api?.url;
  if (!apiUrl) {
    throw new WebApiError(500, 'ApiNotConfigured', 'Web API URL is not configured.');
  }
  return apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
};

const requestJson = async <T>(
  config: RuntimeConfig,
  path: string,
  searchParams?: Record<string, string | undefined>
): Promise<T> => {
  const baseUrl = getWebApiBaseUrl(config);
  const url = new URL(path.replace(/^\//, ''), baseUrl);

  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value === undefined || value === '') {
        continue;
      }
      url.searchParams.set(key, value);
    }
  }

  const token = await ensureIdToken();
  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    let payload: { code?: string; message?: string } | undefined;
    try {
      payload = await response.json() as { code?: string; message?: string };
    } catch {
      payload = undefined;
    }

    const code = payload?.code ?? 'RequestFailed';
    const message = payload?.message ?? `Request failed (${response.status})`;
    throw new WebApiError(response.status, code, message);
  }

  return await response.json() as T;
};

export const listRepos = async (config: RuntimeConfig, tenantId: string): Promise<RepoSummary[]> => {
  const payload = await requestJson<{ repos: RepoSummary[] }>(
    config,
    `/v1/web/tenants/${tenantId}/repos`
  );
  return payload.repos ?? [];
};

export const getRepoEntries = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  path?: string,
  pageToken?: string
): Promise<RepoEntriesResponse> => requestJson<RepoEntriesResponse>(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/entries`,
  {
    path,
    page_token: pageToken
  }
);

export const getRepoStatus = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string
): Promise<RepoStatus> => requestJson<RepoStatus>(
  config,
  `/v1/web/tenants/${tenantId}/repos/${repoId}/status`
);

export const getRepoDevices = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string
): Promise<RepoDevice[]> => {
  const payload = await requestJson<{ devices: RepoDevice[] }>(
    config,
    `/v1/web/tenants/${tenantId}/repos/${repoId}/devices`
  );
  return payload.devices ?? [];
};

export const getDownloadUrl = async (
  config: RuntimeConfig,
  tenantId: string,
  repoId: string,
  path: string
): Promise<string> => {
  const payload = await requestJson<{ url: string }>(
    config,
    `/v1/web/tenants/${tenantId}/repos/${repoId}/download`,
    { path }
  );
  if (!payload.url) {
    throw new WebApiError(500, 'InvalidResponse', 'Download URL is missing.');
  }
  return payload.url;
};
